import type {
    KeyboardEvent as ReactKeyboardEvent,
    MouseEvent as ReactMouseEvent
} from "react";
import { Children, createRef, useCallback, useEffect, useRef } from "react";

import { css } from "@compiled/react";

import { Adg4TabProps } from "./tab";
import { Adg4TabFC, mapChild } from "./tab-helper";
import { useAdg4TabsContext } from "./tabs";

const plainStyle = css({
    display: "flex",
    flex: "0 0 auto",
    cursor: "pointer",
    border: "none",
    backgroundColor: "transparent",
    userSelect: "none"
});

const Adg4DraggableTab: Adg4TabFC<Adg4TabProps> = ({
    id,
    focus,
    tabIndex,
    dataEventProps,
    className,
    children,
    ...props
}: Adg4TabProps) => {
    const { goToTab, isCurrentTab, userSelected } = useAdg4TabsContext();
    const startX = useRef(0);
    const tabRef = createRef<HTMLLIElement>();

    const isTabSelected = isCurrentTab(tabIndex);

    useEffect(() => {
        if (!tabRef.current) {
            return;
        }

        // Scroll to the selected tab if it's not in view
        if (isTabSelected) {
            if (tabRef.current.parentElement) {
                // calculate the offset to center the tab in the parent
                const offsetLeft =
                    tabRef.current.offsetLeft -
                    tabRef.current.parentElement.offsetWidth / 2 +
                    tabRef.current.offsetWidth / 2;

                tabRef.current.parentElement.scrollTo({
                    left: offsetLeft,
                    top: 0,
                    behavior: "smooth"
                });
            }
            if (focus) {
                tabRef.current.focus({
                    preventScroll: true
                });
            }
        }

        if (isTabSelected && dataEventProps && userSelected) {
            tabRef.current.dispatchEvent(new Event("select"));
        }
    }, [tabRef, isTabSelected, focus, userSelected, dataEventProps]);

    const onMouseDown = (e: ReactMouseEvent<HTMLLIElement>) => {
        startX.current = e.clientX ?? 0;
        props.onMouseDown?.(e);
    };

    const onClick = useCallback(
        (e: ReactMouseEvent<HTMLLIElement>) => {
            if (Math.abs(e.clientX - startX.current) > 15) {
                // Prevent click event if user is dragging
                return;
            }
            props.onClick?.(e);

            if (isTabSelected) {
                return;
            }

            goToTab(tabIndex, true);
        },
        [isTabSelected, tabIndex, goToTab, props]
    );

    const onKeyDown = (e: ReactKeyboardEvent) => {
        if (e.code === "Space" || e.code === "Enter") {
            goToTab(tabIndex, true);
            e.preventDefault();
        }
    };

    return (
        <li
            {...props}
            ref={tabRef}
            css={plainStyle}
            className={className}
            id={`tab${id}`}
            aria-selected={isTabSelected}
            aria-controls={`panel${id}`}
            onMouseDown={onMouseDown}
            onClick={onClick}
            onKeyDown={onKeyDown}
            tabIndex={isTabSelected ? 0 : undefined}
            role="tab"
            {...dataEventProps}
        >
            {Children.map(children, (child) => {
                return mapChild(child, { isSelected: isTabSelected });
            })}
        </li>
    );
};
Adg4DraggableTab.tabsRole = "Adg4Tab";

export { Adg4DraggableTab };
